/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import { FiPlus, FiEye, FiEdit2, FiTrash2, FiX } from "react-icons/fi";
import PageContainer from "../components/PageContainer";
import ResponsiveTable from "../components/ResponsiveTable";
import { useUI } from "../theme/ThemeProvider";
import { useTenantStorePermissions } from "../../hooks/useTenantStorePermissions";
import NoAccess from "../components/NoAccess";

// Using colors from useUI instead of hardcoded COLORS

export default function AdminCoupons() {
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const perms = useTenantStorePermissions();

  const baseUrl = (process.env.REACT_APP_API_BASE_URL ||"https://madar-api.madar.iq/").replace(/\/+$/, '');
const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
console.log(tenantId);

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showView, setShowView] = useState(null);
  const [newCoupon, setNewCoupon] = useState({ name: "", discount: "", from: "", to: "" });
  const generateCouponId = () => {
    return Math.floor(Math.random() * 1_000_000) + 1;
  };
  
  // Fetch coupons from API
  useEffect(() => {
    const fetchCoupons = async () => {
      if (!tenantId || tenantId === -1) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        
        
        const res = await fetch(
          `${baseUrl}/Coupons/GetCoupons?tenantId=${tenantId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json",
              "Authorization": token ? `Bearer ${token}` : "",
            },
          }
        );

        let data = [];
        const contentType = res.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          try {
            data = await res.json();
          } catch (parseError) {
            const text = await res.text();
            console.error("Failed to parse GetCoupons JSON response:", parseError);
            console.error("Response text:", text);
            if (text) {
              try {
                data = JSON.parse(text);
              } catch {
                data = [];
              }
            }
          }
        } else {
          const text = await res.text();
          if (text) {
            try {
              data = JSON.parse(text);
            } catch {
              data = [];
            }
          }
        }

        if (!res.ok) {
          const errorMessage = data?.message || data?.error || data?.Message || `HTTP ${res.status}: ${res.statusText}`;
          throw new Error(errorMessage);
        }

        if (Array.isArray(data)) {
          // Map API response to our format
          const mappedCoupons = data.map((c) => {
            // Format dates from ISO to YYYY-MM-DD for input fields
            const formatDate = (dateStr) => {
              if (!dateStr) return "";
              if (dateStr.includes('T')) {
                return dateStr.split('T')[0];
              }
              return dateStr;
            };

            return {
              id: c.id || c.couponId || 0,
              couponId: c.couponId || c.id || 0,
              name: c.couponName || c.name || "",
              discount: c.discount || 0,
              from: formatDate(c.fromDate || c.from || ""),
              to: formatDate(c.toDate || c.to || ""),
              fromDate: c.fromDate || c.from || "",
              toDate: c.toDate || c.to || "",
            };
          });
          
          setCoupons(mappedCoupons);
          console.log("✅ GetCoupons Response:", mappedCoupons);
        } else {
          console.warn("Expected array but got:", typeof data);
          setCoupons([]);
        }
      } catch (err) {
        console.error("GetCoupons error:", err);
        let errorMessage = err.message || String(err);
        
        if (errorMessage.includes("Failed to fetch") || errorMessage.includes("NetworkError")) {
          errorMessage = isArabic 
            ? "فشل الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت" 
            : "Connection failed. Please check your internet connection";
        }
        
        Swal.fire({
          icon: "error",
          title: isArabic ? "خطأ في التحميل ❌" : "Load Error ❌",
          text: errorMessage,
          confirmButtonColor: colors.mainColor,
        });
        setCoupons([]);
      } finally {
        setLoading(false);
      }
    };

    if (tenantId && tenantId !== -1) {
      fetchCoupons();
    } else {
      setLoading(false);
    }
  }, [tenantId, baseUrl, isArabic]);

  // Page-level permission guard (after all hooks)
  if (!perms.viewCoupons) {
    return <NoAccess />;
  }

  const handleAdd = async () => {
    if (!perms.addCoupons) {
      return;
    }
    if (!newCoupon.name.trim() || !newCoupon.discount || !newCoupon.from || !newCoupon.to) {
      return Swal.fire({
        icon: "warning",
        title: isArabic ? "تحذير" : "Warning",
        text: isArabic ? "يرجى إدخال جميع الحقول." : "Please fill in all fields.",
        confirmButtonColor: colors.mainColor,
      });
    }

    try {
      const token = localStorage.getItem("authToken");
      
   

      // Convert dates to ISO format
      const fromDate = newCoupon.from ? new Date(newCoupon.from + 'T00:00:00').toISOString() : new Date().toISOString();
      const toDate = newCoupon.to ? new Date(newCoupon.to + 'T23:59:59').toISOString() : new Date().toISOString();
      const couponId = generateCouponId();

      const payload = {
        id: 0,
        couponId:Number(couponId),
        TenantId: tenantId,
        couponName: newCoupon.name.trim(),
        discount: parseFloat(newCoupon.discount) || 0,
        fromDate: fromDate,
        toDate: toDate,
      };

      const res = await fetch(`${baseUrl}/Coupons/AddCoupon`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(payload),
      });

      let data = {};
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          data = await res.json();
        } catch (parseError) {
          const text = await res.text();
          console.error("Failed to parse AddCoupon JSON response:", parseError);
          console.error("Response text:", text);
          if (text) {
            try {
              data = JSON.parse(text);
            } catch {
              data = { message: text || `HTTP ${res.status}: ${res.statusText}` };
            }
          }
        }
      } else {
        const text = await res.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch {
            data = { message: text || `HTTP ${res.status}: ${res.statusText}` };
          }
        }
      }

      if (!res.ok) {
        const errorMessage = data?.message || data?.error || data?.Message || (isArabic
          ? "فشل إضافة الكوبون ❌"
          : "Failed to add coupon ❌");

        if (data?.errors) {
          const validationErrors = Object.entries(data.errors)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`)
            .join("\n");
          console.error("❌ Validation Errors:", validationErrors);
          throw new Error(isArabic
            ? `خطأ في التحقق من البيانات:\n${validationErrors}`
            : `Validation Error:\n${validationErrors}`);
        }
        
        throw new Error(errorMessage);
      }

      console.log("✅ AddCoupon Response:", data);
      
      // Refresh coupons list
      const fetchCoupons = async () => {
        const res = await fetch(
          `${baseUrl}/Coupons/GetCoupons?tenantId=${tenantId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json",
              "Authorization": token ? `Bearer ${token}` : "",
            },
          }
        );

        if (res.ok) {
          const couponsData = await res.json().catch(() => []);
          if (Array.isArray(couponsData)) {
            const mappedCoupons = couponsData.map((c) => {
              const formatDate = (dateStr) => {
                if (!dateStr) return "";
                if (dateStr.includes('T')) {
                  return dateStr.split('T')[0];
                }
                return dateStr;
              };

              return {
                id: c.id || c.couponId || 0,
                couponId: c.couponId || c.id || 0,
                name: c.couponName || c.name || "",
                discount: c.discount || 0,
                from: formatDate(c.fromDate || c.from || ""),
                to: formatDate(c.toDate || c.to || ""),
                fromDate: c.fromDate || c.from || "",
                toDate: c.toDate || c.to || "",
              };
            });
            setCoupons(mappedCoupons);
          }
        }
      };
      await fetchCoupons();

      setShowAdd(false);
      setNewCoupon({ name: "", discount: "", from: "", to: "" });

      Swal.fire({
        icon: "success",
        title: isArabic ? "تمت الإضافة ✅" : "Coupon Added ✅",
        text: data?.message || (isArabic ? "تم إنشاء الكوبون بنجاح." : "Coupon created successfully."),
        confirmButtonColor: colors.mainColor,
        timer: 1500,
      });
    } catch (err) {
      console.error("AddCoupon error:", err);
      
      let errorMessage = err.message || String(err);
      
      if (errorMessage.includes("Failed to fetch") || errorMessage.includes("NetworkError")) {
        errorMessage = isArabic 
          ? "فشل الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت" 
          : "Connection failed. Please check your internet connection";
      } else if (errorMessage.includes("401") || errorMessage.toLowerCase().includes("unauthorized")) {
        errorMessage = isArabic 
          ? "غير مصرح. يرجى تسجيل الدخول مرة أخرى" 
          : "Unauthorized. Please login again";
      } else if (errorMessage.includes("400")) {
        errorMessage = isArabic 
          ? "بيانات غير صحيحة. يرجى التحقق من الحقول المطلوبة" 
          : "Invalid data. Please check required fields";
      } else if (errorMessage.includes("500")) {
        errorMessage = isArabic 
          ? "خطأ في الخادم. يرجى المحاولة لاحقاً" 
          : "Server error. Please try again later";
      }
      
      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل الإضافة ❌" : "Add Failed ❌",
        text: errorMessage,
        confirmButtonColor: colors.mainColor,
      });
    }
  };

  const handleEditSave = async () => {
    if (!perms.editCoupons) {
      return;
    }
    if (!showEdit.name.trim() || !showEdit.discount || !showEdit.from || !showEdit.to) {
      return Swal.fire({
        icon: "warning",
        title: isArabic ? "تحذير" : "Warning",
        text: isArabic ? "يرجى إدخال جميع الحقول." : "Please fill in all fields.",
        confirmButtonColor: colors.mainColor,
      });
    }

    try {
      const token = localStorage.getItem("authToken");
      


      // Convert dates to ISO format
      const fromDate = showEdit.from ? new Date(showEdit.from + 'T00:00:00').toISOString() : new Date().toISOString();
      const toDate = showEdit.to ? new Date(showEdit.to + 'T23:59:59').toISOString() : new Date().toISOString();

      const couponIdForUpdate = showEdit.couponId || showEdit.id;

      const payload = {
        id: couponIdForUpdate, // Use id, not couponId (because couponId is always 0 from API)
        couponId: couponIdForUpdate, // Set to same value as id (API might need this, but we use id as source)
        tenantId: tenantId,
        couponName: showEdit.name.trim(),
        discount: parseFloat(showEdit.discount) || 0,
        fromDate: fromDate,
        toDate: toDate,
      };

      // Use id (not couponId) in query parameter because couponId is always 0
      const res = await fetch(`${baseUrl}/Coupons/UpdateCoupon?couponId=${couponIdForUpdate}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(payload),
      });

      let data = {};
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          data = await res.json();
        } catch (parseError) {
          const text = await res.text();
          console.error("Failed to parse UpdateCoupon JSON response:", parseError);
          console.error("Response text:", text);
          if (text) {
            try {
              data = JSON.parse(text);
            } catch {
              data = { message: text || `HTTP ${res.status}: ${res.statusText}` };
            }
          }
        }
      } else {
        const text = await res.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch {
            data = { message: text || `HTTP ${res.status}: ${res.statusText}` };
          }
        }
      }

      if (!res.ok) {
        const errorMessage = data?.message || data?.error || data?.Message || (isArabic
          ? "فشل تحديث الكوبون ❌"
          : "Failed to update coupon ❌");

        if (data?.errors) {
          const validationErrors = Object.entries(data.errors)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`)
            .join("\n");
          console.error("❌ Validation Errors:", validationErrors);
          throw new Error(isArabic
            ? `خطأ في التحقق من البيانات:\n${validationErrors}`
            : `Validation Error:\n${validationErrors}`);
        }
        
        throw new Error(errorMessage);
      }

      console.log("✅ UpdateCoupon Response:", data);
      
      // Refresh coupons list
      const fetchCoupons = async () => {
        const res = await fetch(
          `${baseUrl}/Coupons/GetCoupons?tenantId=${tenantId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json",
              "Authorization": token ? `Bearer ${token}` : "",
            },
          }
        );

        if (res.ok) {
          const couponsData = await res.json().catch(() => []);
          if (Array.isArray(couponsData)) {
            const mappedCoupons = couponsData.map((c) => {
              const formatDate = (dateStr) => {
                if (!dateStr) return "";
                if (dateStr.includes('T')) {
                  return dateStr.split('T')[0];
                }
                return dateStr;
              };

              return {
                id: c.id || c.couponId || 0,
                couponId: c.couponId || c.id || 0,
                name: c.couponName || c.name || "",
                discount: c.discount || 0,
                from: formatDate(c.fromDate || c.from || ""),
                to: formatDate(c.toDate || c.to || ""),
                fromDate: c.fromDate || c.from || "",
                toDate: c.toDate || c.to || "",
              };
            });
            setCoupons(mappedCoupons);
          }
        }
      };
      await fetchCoupons();

      setShowEdit(null);
      
      Swal.fire({
        icon: "success",
        title: isArabic ? "تم الحفظ ✅" : "Saved ✅",
        text: data?.message || (isArabic ? "تم تعديل الكوبون بنجاح." : "Coupon updated successfully."),
        confirmButtonColor: colors.mainColor,
        timer: 1500,
      });
    } catch (err) {
      console.error("UpdateCoupon error:", err);
      
      let errorMessage = err.message || String(err);
      
      if (errorMessage.includes("Failed to fetch") || errorMessage.includes("NetworkError")) {
        errorMessage = isArabic 
          ? "فشل الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت" 
          : "Connection failed. Please check your internet connection";
      } else if (errorMessage.includes("401") || errorMessage.toLowerCase().includes("unauthorized")) {
        errorMessage = isArabic 
          ? "غير مصرح. يرجى تسجيل الدخول مرة أخرى" 
          : "Unauthorized. Please login again";
      } else if (errorMessage.includes("400")) {
        // Parse validation errors from response
        const validationErrorMatch = errorMessage.match(/خطأ في التحقق من البيانات:|Validation Error:/);
        if (validationErrorMatch) {
          // Keep the detailed validation error message
        } else {
          errorMessage = isArabic 
            ? "بيانات غير صحيحة. يرجى التحقق من المعرفات والحقول المطلوبة (id و tenantId)" 
            : "Invalid data. Please check required fields (id and tenantId)";
        }
      } else if (errorMessage.includes("404")) {
        errorMessage = isArabic 
          ? "الكوبون غير موجود" 
          : "Coupon not found";
      } else if (errorMessage.includes("500")) {
        errorMessage = isArabic 
          ? "خطأ في الخادم. يرجى المحاولة لاحقاً" 
          : "Server error. Please try again later";
      }
      
      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل التحديث ❌" : "Update Failed ❌",
        text: errorMessage,
        confirmButtonColor: colors.mainColor,
      });
    }
  };

  const handleDelete = async (id) => {
    if (!perms.deleteCoupons) {
      return;
    }
    const coupon = coupons.find((c) => c.id === id);
    if (!coupon) return;

    const result = await Swal.fire({
      title: isArabic ? "هل أنت متأكد؟" : "Are you sure?",
      text: isArabic
        ? `سيتم حذف الكوبون "${coupon.name}" بشكل نهائي!`
        : `Coupon "${coupon.name}" will be permanently deleted!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: colors.red,
      cancelButtonColor: colors.gold,
      confirmButtonText: isArabic ? "نعم، احذف!" : "Yes, delete it!",
      cancelButtonText: isArabic ? "إلغاء" : "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      // Use only 'id' field (not 'couponId' because it's always 0 from API)
      // API response shows: id: 1, couponId: 0
      const couponIdToDelete = coupon.id || id;
      
      // Validate id and tenantId - id must NOT be 0
      if (!couponIdToDelete || couponIdToDelete === 0) {
        throw new Error(isArabic ? "معرف الكوبون غير صحيح (لا يمكن أن يكون 0)" : "Invalid coupon ID (cannot be 0)");
      }

      if (!tenantId || tenantId === -1) {
        throw new Error(isArabic ? "معرف المتجر غير صحيح" : "Invalid tenant ID");
      }

      const token = localStorage.getItem("authToken");
      

      // API expects 'Id' (capital I) and 'tenantId' (lowercase t)
      // Use coupon.id (not coupon.couponId) because couponId is always 0
      const res = await fetch(`${baseUrl}/Coupons/DeleteCoupon?Couponid=${coupon.couponId}&tenantId=${tenantId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
      });

      let data = {};
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          data = await res.json();
        } catch (parseError) {
          const text = await res.text();
          console.error("Failed to parse DeleteCoupon JSON response:", parseError);
          console.error("Response text:", text);
          if (text) {
            try {
              data = JSON.parse(text);
            } catch {
              data = { message: text || `HTTP ${res.status}: ${res.statusText}` };
            }
          }
        }
      } else {
        const text = await res.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch {
            data = { message: text || `HTTP ${res.status}: ${res.statusText}` };
          }
        }
      }

      if (!res.ok) {
        console.error("❌ DeleteCoupon Response Status:", res.status);
        console.error("❌ DeleteCoupon Response Data:", data);

        let errorMessage = data?.message || data?.error || data?.Message || (isArabic
          ? "فشل حذف الكوبون ❌"
          : "Failed to delete coupon ❌");

        // Check for validation errors
        if (data?.errors) {
          const validationErrors = Object.entries(data.errors)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`)
            .join("\n");
          console.error("❌ Validation Errors:", validationErrors);
          errorMessage = isArabic
            ? `خطأ في التحقق من البيانات:\n${validationErrors}`
            : `Validation Error:\n${validationErrors}`;
        }

        // Check for specific error messages
        if (data?.title || data?.type) {
          console.error("❌ Error Details:", {
            type: data.type,
            title: data.title,
            status: data.status,
            traceId: data.traceId
          });
        }

        throw new Error(errorMessage);
      }

      console.log("✅ DeleteCoupon Response:", data);
      
      // Refresh coupons list
      const fetchCoupons = async () => {
        const res = await fetch(
          `${baseUrl}/Coupons/GetCoupons?tenantId=${tenantId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json",
              "Authorization": token ? `Bearer ${token}` : "",
            },
          }
        );

        if (res.ok) {
          const couponsData = await res.json().catch(() => []);
          if (Array.isArray(couponsData)) {
            const mappedCoupons = couponsData.map((c) => {
              const formatDate = (dateStr) => {
                if (!dateStr) return "";
                if (dateStr.includes('T')) {
                  return dateStr.split('T')[0];
                }
                return dateStr;
              };

              return {
                id: c.id || c.couponId || 0,
                couponId: c.couponId || c.id || 0,
                name: c.couponName || c.name || "",
                discount: c.discount || 0,
                from: formatDate(c.fromDate || c.from || ""),
                to: formatDate(c.toDate || c.to || ""),
                fromDate: c.fromDate || c.from || "",
                toDate: c.toDate || c.to || "",
              };
            });
            setCoupons(mappedCoupons);
          }
        }
      };
      await fetchCoupons();
      
      Swal.fire({
        icon: "success",
        title: isArabic ? "تم الحذف! ✅" : "Deleted! ✅",
        text: data?.message || (isArabic
          ? "تم حذف الكوبون بنجاح."
          : "Coupon deleted successfully."),
        confirmButtonColor: colors.mainColor,
        timer: 1500,
      });
    } catch (err) {
      console.error("DeleteCoupon error:", err);
      
      let errorMessage = err.message || String(err);
      
      if (errorMessage.includes("Failed to fetch") || errorMessage.includes("NetworkError")) {
        errorMessage = isArabic 
          ? "فشل الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت" 
          : "Connection failed. Please check your internet connection";
      } else if (errorMessage.includes("401") || errorMessage.toLowerCase().includes("unauthorized")) {
        errorMessage = isArabic 
          ? "غير مصرح. يرجى تسجيل الدخول مرة أخرى" 
          : "Unauthorized. Please login again";
      } else if (errorMessage.includes("404")) {
        errorMessage = isArabic 
          ? "الكوبون غير موجود" 
          : "Coupon not found";
      } else if (errorMessage.includes("500")) {
        errorMessage = isArabic 
          ? "خطأ في الخادم. يرجى المحاولة لاحقاً" 
          : "Server error. Please try again later";
      }
      
      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل الحذف ❌" : "Delete Failed ❌",
        text: errorMessage,
        confirmButtonColor: colors.mainColor,
      });
    }
  };

  return (
    <PageContainer title={isArabic ? "كوبونات الخصم" : "Discount Coupons"}>
  
      {/* ===== Loading ===== */}
      {loading && (
        <div className="text-center py-12">
          <div
            className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2"
            style={{ borderColor: colors.mainColor }}
          />
          <p className="mt-4" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>
            {isArabic ? "جاري تحميل الكوبونات..." : "Loading coupons..."}
          </p>
        </div>
      )}
  
      {!loading && (
        <>
            <div className="w-full overflow-x-auto">
              <ResponsiveTable
                columns={[
                  { header: isArabic ? "اسم الكوبون" : "Coupon Name", sortKey: "name" },
                  { header: isArabic ? "نسبة الخصم" : "Discount %", sortKey: "discount" },
                  { header: isArabic ? "من" : "From", sortKey: "from" },
                  { header: isArabic ? "إلى" : "To", sortKey: "to" },
                  { header: isArabic ? "تحكم" : "Actions" },
                ]}
                data={coupons}
                searchable
                sortable
                headerActions={
                  perms.addCoupons && (
                    <button
                      onClick={() => setShowAdd(true)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold shadow"
                      style={{ background: colors.mainColor }}
                    >
                      <FiPlus size={16} />
                      {isArabic ? "إنشاء كوبون" : "Add Coupon"}
                    </button>
                  )
                }
                emptyMessage={
                  isArabic ? "لا توجد كوبونات متاحة" : "No coupons available"
                }
                renderRow={(c) => (
                  <>
                    <td className="py-3 px-4 whitespace-nowrap" style={{ textAlign: isArabic ? "right" : "left" }}>
                      {c.name || "—"}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap" style={{ textAlign: isArabic ? "right" : "left", color: colors.gold }}>
                      {c.discount}%
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap" style={{ textAlign: isArabic ? "right" : "left" }}>
                      {c.from || "—"}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap" style={{ textAlign: isArabic ? "right" : "left" }}>
                      {c.to || "—"}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <button onClick={() => setShowView(c)} style={{ background: colors.green }} className="p-2 rounded text-white">
                          <FiEye size={16} />
                        </button>
                        {perms.editCoupons && (
                          <button onClick={() => setShowEdit(c)} style={{ background: colors.gold }} className="p-2 rounded text-white">
                            <FiEdit2 size={16} />
                          </button>
                        )}
                        {perms.deleteCoupons && (
                          <button onClick={() => handleDelete(c.id)} style={{ background: colors.red }} className="p-2 rounded text-white">
                            <FiTrash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
 
                  </>
                
                )}
              />
            </div>
        </>
      )}
  
      {/* ===== المودالات ===== */}
      <AnimatePresence>
        {(showAdd || showEdit) && (
          <CouponModal
            isArabic={isArabic}
            isDark={isDark}
            colors={colors}
            title={showAdd ? (isArabic ? "إضافة كوبون جديد" : "Add Coupon") : isArabic ? "تعديل الكوبون" : "Edit Coupon"}
            coupon={showAdd ? newCoupon : showEdit}
            setCoupon={showAdd ? setNewCoupon : setShowEdit}
            onSave={showAdd ? handleAdd : handleEditSave}
            onClose={() => {
              setShowAdd(false);
              setShowEdit(null);
            }}
          />
        )}
      </AnimatePresence>
  
      <AnimatePresence>
        {showView && (
          <ViewCouponModal
            isArabic={isArabic}
            isDark={isDark}
            colors={colors}
            coupon={showView}
            onClose={() => setShowView(null)}
          />
        )}
      </AnimatePresence>
  
    </PageContainer>
  );  
}

/* ===== مودال الإضافة / التعديل ===== */
function CouponModal({ isArabic, isDark, colors, title, coupon, setCoupon, onSave, onClose }) {
  const { colors: uiColors } = useUI();
  const finalColors = colors || uiColors;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[9999]"
    >
      <div
        className="relative w-[95%] sm:w-[90%] max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl p-6"
        style={{ 
          background: isDark ? "#1E2533" : "#ffffff", 
          border: isDark ? "1px solid rgba(255,255,255,0.25)" : "1px solid rgba(0,0,0,0.1)",
          color: isDark ? "#f1f5f9" : "#111827"
        }}
      >
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-bold" style={{ color: finalColors.mainColor }}>
            {title}
          </h2>
          <button 
            onClick={onClose} 
            className="transition"
            style={{ color: isDark ? "#9ca3af" : "#6b7280" }}
            onMouseEnter={(e) => e.currentTarget.style.color = "#ef4444"}
            onMouseLeave={(e) => e.currentTarget.style.color = isDark ? "#9ca3af" : "#6b7280"}
          >
            <FiX size={22} />
          </button>
        </div>

        {["name", "discount", "from", "to"].map((field, i) => (
          <div key={i} className="mb-4">
            <label 
              className="block mb-2 font-semibold"
              style={{ color: isDark ? "#f1f5f9" : "#111827" }}
            >
              {{
                name: isArabic ? "اسم الكوبون" : "Coupon Name",
                discount: isArabic ? "نسبة الخصم (%)" : "Discount (%)",
                from: isArabic ? "تاريخ البداية" : "Start Date",
                to: isArabic ? "تاريخ النهاية" : "End Date",
              }[field]}
            </label>
            <input
              type={field === "discount" ? "number" : field === "from" || field === "to" ? "date" : "text"}
              value={coupon[field]}
              onChange={(e) => setCoupon({ ...coupon, [field]: e.target.value })}
              className="w-full rounded-lg px-3 py-2 focus:outline-none focus:border-purple-400 transition"
              style={{ 
                background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
                border: isDark ? "1px solid rgba(255,255,255,0.25)" : "1px solid rgba(0,0,0,0.15)",
                color: isDark ? "#f1f5f9" : "#111827"
              }}
            />
          </div>
        ))}

        <button
          onClick={onSave}
          className="w-full py-2 mt-2 rounded-lg text-white font-semibold shadow-md transition"
          style={{ background: finalColors.mainColor }}
        >
          {isArabic ? "حفظ" : "Save"}
        </button>
      </div>
    </motion.div>
  );
}

/* ===== مودال المعاينة ===== */
function ViewCouponModal({ isArabic, isDark, colors, coupon, onClose }) {
  const { colors: uiColors } = useUI();
  const finalColors = colors || uiColors;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[9999]"
    >
      <div
        className="w-[95%] sm:w-[90%] max-w-md rounded-2xl shadow-xl p-6"
        style={{ 
          background: isDark ? "#1E2533" : "#ffffff", 
          border: isDark ? "1px solid rgba(255,255,255,0.25)" : "1px solid rgba(0,0,0,0.1)",
          color: isDark ? "#f1f5f9" : "#111827"
        }}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold" style={{ color: finalColors.mainColor }}>
            {isArabic ? "تفاصيل الكوبون" : "Coupon Details"}
          </h2>
          <FiX 
            onClick={onClose} 
            className="cursor-pointer transition"
            style={{ color: isDark ? "#9ca3af" : "#6b7280" }}
            onMouseEnter={(e) => e.currentTarget.style.color = "#ef4444"}
            onMouseLeave={(e) => e.currentTarget.style.color = isDark ? "#9ca3af" : "#6b7280"}
          />
        </div>
        <div 
          className="space-y-3 text-center"
          style={{ color: isDark ? "#f1f5f9" : "#111827" }}
        >
          <p>
            <strong>{isArabic ? "الاسم:" : "Name:"}</strong> {coupon.name}
          </p>
          <p>
            <strong>{isArabic ? "الخصم:" : "Discount:"}</strong> {coupon.discount}%
          </p>
          <p>
            <strong>{isArabic ? "من:" : "From:"}</strong> {coupon.from}
          </p>
          <p>
            <strong>{isArabic ? "إلى:" : "To:"}</strong> {coupon.to}
          </p>
        </div>
      </div>
    </motion.div>
  );
}
